/**
 * «Ver como» — a troca entre a Genesis e as vistas do portal (issue #584; Stories 35.9
 * e 35.10).
 *
 * Vive DENTRO do menu do usuário dos dois shells (o rodapé da sidebar interna e o
 * `PortalUserMenu`), e só aparece a quem tem o que escolher: o perfil de cliente
 * (membro de um cliente) e/ou os clientes de que é gestora de conta. O gestor de conta
 * substituiu o suporte, que abria qualquer cliente ativo: a lista vem da `/api/me`
 * (`portalViews.managedCustomers`), já só com os ativos, e escolhe-se no perfil.
 *
 * A TROCA É UM RECARREGAMENTO, e não uma navegação do router: os dois shells
 * assumem que se chega a eles por um documento novo (`html[data-surface]`, o
 * `--aur-h` do `AppLayout`), e as caches do portal de um cliente não podem
 * sobreviver para dentro do de outro. A vista é da ABA (`lib/portal-view.ts`).
 *
 * OS CLIENTES GERIDOS FICAM AQUI DENTRO, numa lista em linha que rola: costumam ser
 * poucos.
 *
 * O OWNER (Story 35.10) abre qualquer cliente ativo, e entra sempre como owner: em vez
 * do perfil e dos geridos, tem um item que abre a PÁGINA DA LISTA (#736), o
 * `OwnerCustomerPicker`, com TODOS os clientes ativos, busca e teclado. A página troca
 * o conteúdo inteiro do menu, e quem a monta é o shell: este item só a pede
 * (`onOpenCustomerPicker`). Não há segundo overlay por cima do menu: o menu é um
 * overlay de vidro, e um segundo aberto a partir dele seria um popper dentro de
 * `backdrop-filter` (o recorte que o `vidro-do-chrome.test.ts` documenta) ou um menu
 * que fecha por baixo do diálogo. A lista é a de `GET /api/customers`, que o admin já
 * lê, e só se consulta com a página montada.
 */
import {
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Check,
  ChevronRight,
  Crown,
  LayoutDashboard,
  type LucideIcon,
} from 'lucide-react'
import type { PortalMode, PortalViews } from '@caliber/shared'

import { SearchField } from '@/components/lumen/search-field'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { customerLogoUrl, listCustomers } from '@/lib/api/customers'
import { choosePortalView, leavePortalView } from '@/lib/portal-view'
import { GENESIS_HOME, PORTAL_HOME } from '@/lib/roles'
import { CustomerLogo } from '@/routes/_authenticated/customers/components/customer-logo'

type VistaAtual = { customerId: string; customerName: string; mode: PortalMode } | null

export interface PortalViewSwitcherProps {
  views: PortalViews
  /** Onde a aba está: na Genesis (`null`), ou na vista de um cliente. */
  current: VistaAtual
  /** Fecha o menu — antes de a página recarregar, ou quando nada muda. */
  onNavigate: () => void
  /** O owner pede a página da lista; o shell troca o conteúdo do menu por ela. */
  onOpenCustomerPicker: () => void
  /** Depois do «Voltar» da página da lista: o item do owner recebe o foco. */
  returnFocus?: boolean
}

function irParaGenesis(current: VistaAtual, onNavigate: () => void): void {
  onNavigate()
  if (current === null) return
  leavePortalView()
  window.location.assign(GENESIS_HOME)
}

function irParaCliente(customerId: string, current: VistaAtual, onNavigate: () => void): void {
  onNavigate()
  if (current?.customerId === customerId) return
  choosePortalView(customerId)
  window.location.assign(PORTAL_HOME)
}

function Item({
  icon: Icon,
  label,
  hint,
  atual,
  chevron,
  autoFocus,
  onSelect,
}: {
  icon: LucideIcon
  label: ReactNode
  hint?: string
  atual: boolean
  /** O item abre outra página do menu (a lista do owner). */
  chevron?: boolean
  autoFocus?: boolean
  onSelect: () => void
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={atual ? 'true' : undefined}
      autoFocus={autoFocus}
      className="text-ink hover:bg-sunk flex w-full items-center gap-2.5 rounded-sm px-2.5 py-2 text-left text-[13px] font-medium transition-colors"
    >
      <Icon className="text-ink-3 size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block truncate">{label}</span>
        {hint ? <span className="text-ink-3 block truncate text-[11px]">{hint}</span> : null}
      </span>
      {atual ? <Check className="text-ink-2 size-4 shrink-0" aria-hidden="true" /> : null}
      {chevron && !atual ? (
        <ChevronRight className="text-ink-3 size-4 shrink-0" aria-hidden="true" />
      ) : null}
    </button>
  )
}

/**
 * A página da lista do owner (#736): troca o conteúdo inteiro do menu do nome. O cliente
 * da vista atual fica de fora, porque já está marcado no menu; o do perfil de cliente
 * entra, porque o owner entra nele como owner.
 *
 * O TECLADO é só de setas: ↓ desce da busca à primeira linha, ↓ e ↑ andam entre as
 * linhas (sem dar a volta), e ↑ na primeira volta à busca. Enter é o do `<button>`, e
 * Esc fecha o menu, que é o padrão do Radix. São botões numa lista, e não um `listbox`:
 * escolher um cliente é navegar, não selecionar um valor.
 */
export function OwnerCustomerPicker({
  current,
  onBack,
  onNavigate,
}: {
  current: VistaAtual
  /** Volta ao menu. */
  onBack: () => void
  onNavigate: () => void
}): ReactElement {
  const [busca, setBusca] = useState('')
  const buscaRef = useRef<HTMLInputElement>(null)
  const listaRef = useRef<HTMLUListElement>(null)

  const clientes = useQuery({
    queryKey: ['customers', 'portal-view-picker'],
    queryFn: ({ signal }) => listCustomers({ status: 'active', pageSize: 200, signal }),
  })

  const opcoes = useMemo(() => {
    const termo = busca.trim().toLocaleLowerCase('pt-BR')
    return (clientes.data?.items ?? []).filter(
      (c) =>
        c.id !== current?.customerId &&
        (termo === '' || c.name.toLocaleLowerCase('pt-BR').includes(termo)),
    )
  }, [clientes.data, busca, current])

  function linhas(): HTMLButtonElement[] {
    return Array.from(listaRef.current?.querySelectorAll<HTMLButtonElement>('[data-cliente]') ?? [])
  }

  function naBusca(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key !== 'ArrowDown') return
    e.preventDefault()
    linhas()[0]?.focus()
  }

  function naLista(e: KeyboardEvent<HTMLUListElement>): void {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    const todas = linhas()
    const i = todas.indexOf(e.target as HTMLButtonElement)
    if (i === -1) return
    e.preventDefault()
    if (e.key === 'ArrowDown') todas[Math.min(i + 1, todas.length - 1)]?.focus()
    else if (i === 0) buscaRef.current?.focus()
    else todas[i - 1]?.focus()
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 pb-2">
        <Button variant="ghost" size="sm" onClick={onBack} className="px-2.5">
          <ArrowLeft aria-hidden="true" />
          Voltar
        </Button>
        <p className="text-ink truncate text-[13px] font-semibold">Abrir como owner</p>
      </div>

      <div className="px-1.5">
        <SearchField
          value={busca}
          onChange={setBusca}
          label="Buscar cliente"
          placeholder="Buscar cliente"
          className="w-full"
          autoFocus
          onKeyDown={naBusca}
          inputRef={buscaRef}
        />
      </div>

      <div className="mt-1.5">
        {clientes.isPending ? (
          <div className="space-y-1.5 p-1.5" aria-busy="true">
            <Skeleton className="h-7 w-full" />
            <Skeleton className="h-7 w-5/6" />
            <Skeleton className="h-7 w-4/6" />
          </div>
        ) : clientes.isError ? (
          <div className="space-y-1.5 p-1.5">
            <p role="alert" className="text-error text-[12px]">
              Não foi possível carregar os clientes.
            </p>
            <Button size="sm" variant="secondary" onClick={() => void clientes.refetch()}>
              Tentar de novo
            </Button>
          </div>
        ) : opcoes.length === 0 ? (
          <p className="text-ink-2 p-2.5 text-[12px]">
            {busca.trim() === '' ? 'Nenhum outro cliente ativo.' : 'Nenhum cliente com esse nome.'}
          </p>
        ) : (
          <ul
            ref={listaRef}
            aria-label="Clientes ativos"
            onKeyDown={naLista}
            className="max-h-[min(22rem,55vh)] overflow-y-auto"
          >
            {opcoes.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  data-cliente=""
                  onClick={() => irParaCliente(c.id, current, onNavigate)}
                  className="item-glass text-ink flex w-full items-center gap-2.5 rounded-sm px-2.5 py-1.5 text-left text-[13px]"
                >
                  <CustomerLogo
                    name={c.name}
                    src={c.logoKey ? customerLogoUrl(c.id, c.updatedAt) : null}
                    size={22}
                  />
                  <span className="min-w-0 flex-1 truncate">{c.name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export function PortalViewSwitcher({
  views,
  current,
  onNavigate,
  onOpenCustomerPicker,
  returnFocus,
}: PortalViewSwitcherProps): ReactElement {
  const perfil = views.clientCustomer
  // O owner entra sempre como owner (Story 35.10): a API já não lhe dá os modos de
  // perfil nem de gestor, e os itens deles enganariam.
  const owner = views.owner === true
  const naVistaDeOwner = owner && current?.mode === 'owner'

  return (
    <div>
      <p className="eyebrow px-1.5 pb-1.5 pt-0.5">Ver como</p>

      <Item
        icon={LayoutDashboard}
        label="Genesis Caliber"
        atual={current === null}
        onSelect={() => irParaGenesis(current, onNavigate)}
      />

      {naVistaDeOwner ? (
        <Item icon={Crown} label={`Owner · ${current.customerName}`} atual onSelect={onNavigate} />
      ) : null}

      {owner ? (
        <Item
          icon={Crown}
          label={naVistaDeOwner ? 'Trocar de cliente…' : 'Abrir um cliente como owner…'}
          hint="Todos os clientes ativos"
          atual={false}
          chevron
          autoFocus={returnFocus === true}
          onSelect={onOpenCustomerPicker}
        />
      ) : null}

      {!owner && perfil ? (
        <Item
          icon={Building2}
          label={perfil.name}
          hint="Perfil de cliente"
          atual={current?.mode === 'member'}
          onSelect={() => irParaCliente(perfil.id, current, onNavigate)}
        />
      ) : null}

      {!owner && views.managedCustomers.length > 0 ? (
        <div className="max-h-56 overflow-y-auto">
          {views.managedCustomers.map((c) => (
            <Item
              key={c.id}
              icon={Briefcase}
              label={c.name}
              hint="Gestor de conta"
              atual={current?.mode === 'account_manager' && current.customerId === c.id}
              onSelect={() => irParaCliente(c.id, current, onNavigate)}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
